import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import SideNav from "../../components/layout/sideNav";
import Icon from "../../components/ui/icon";
import api from "../../services/apiClient";
import moment from "moment-timezone";
import { useToast } from "../../context/toastContext";
import "../../assets/styles/pages/publishers/postbackLogs.css";
import { getTimezoneByCountry } from "../../utils/getTimezoneByCountry";

const LIMIT = 30;

const PostbackLogsPage = () => {
    const navigate = useNavigate();
    const showToast = useToast();

    const [isSideNavActive, setIsSideNavActive] = useState(false);
    const [userInfo, setUserInfo] = useState(
        JSON.parse(localStorage.getItem("PublisherInfo")) || {}
    );

    const [logs, setLogs] = useState([]);
    const [skip, setSkip] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(true);
    const country = userInfo?.country || "Unknown";

    const isFetchingRef = useRef(false);
    const userVerifiedRef = useRef(false);

    const fetchLogs = React.useCallback(
        async (initial = false) => {
            if (isFetchingRef.current || !hasMore) return;

            isFetchingRef.current = true;
            setLoading(true);

            try {
                const res = await api.get(
                    `/api/postback/logs?limit=${LIMIT}&skip=${initial ? 0 : skip}`
                );

                if (res.data.status) {
                    const fetchedLogs = res.data.logs;

                    setLogs((prev) =>
                        initial ? fetchedLogs : [...prev, ...fetchedLogs]
                    );

                    setSkip((prev) => prev + LIMIT);
                    if (fetchedLogs.length < LIMIT) setHasMore(false);
                }
            } catch {
                showToast({ status: "error", message: "Failed to load logs" });
            } finally {
                isFetchingRef.current = false;
                setLoading(false);
            }
        },
        [hasMore, skip, showToast]
    );

    useEffect(() => {
        const token = localStorage.getItem("aToken");
        if (!token) return navigate("/auth/login");

        if (userVerifiedRef.current) return;

        api
            .get("/api/publisher/info")
            .then((res) => {
                setUserInfo(res.data.data);
                localStorage.setItem(
                    "PublisherInfo",
                    JSON.stringify(res.data.data)
                );
                userVerifiedRef.current = true;
                fetchLogs(true);
            })
            .catch(() => {
                showToast({ status: "error", message: "Session expired" });
                navigate("/auth/login");
            });
    }, [navigate, showToast, fetchLogs]);

    useEffect(() => {
        const onScroll = () => {
            if (
                window.innerHeight + window.scrollY >=
                document.body.offsetHeight - 200
            ) {
                fetchLogs();
            }
        };

        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, [fetchLogs]);

    return (
        <div className="postback_logs__page">
            <SideNav
                userName={userInfo.name}
                emailID={userInfo.email}
                active={isSideNavActive}
                campaignOpenInital={false}
                accManagerName={userInfo.adminInfo?.name}
                accManagerEmail={userInfo.adminInfo?.email}
            />

            <div
                className={`background-shadow ${isSideNavActive ? "active" : ""}`}
                onClick={() => setIsSideNavActive(false)}
            />

            <main>
                <header className="desktop-header">
                    <div>
                        <h2>Postback Logs</h2>
                        <p>Track all fired, successful, and failed postbacks</p>
                    </div>
                </header>

                <header className="mobile-header">
                    <h2>Postback Logs</h2>
                    <button
                        className="nav-btn"
                        onClick={() => setIsSideNavActive(!isSideNavActive)}
                    >
                        <Icon name={isSideNavActive ? "XMark" : "Bars3"} />
                    </button>
                </header>

                <div className="logs-viewport">
                    <div className="logs-table-container">
                        <table className="logs-table">
                            <thead>
                                <tr>
                                    <th>Time</th>
                                    <th>Campaign</th>
                                    <th>Goal</th>
                                    <th>Level</th>
                                    <th>Status</th>
                                    <th>HTTP</th>
                                    <th>Latency</th>
                                </tr>
                            </thead>

                            <tbody>
                                {logs.map((log) => (
                                    <tr key={log.logId}>
                                        <td className="time">
                                            {moment
                                                .tz(
                                                    log.firedAt,
                                                    getTimezoneByCountry(country)
                                                )
                                                .format("DD MMM YYYY, h:mm A")}
                                        </td>

                                        <td className="campaign">
                                            <span className="campaign-name">
                                                {log.campaignId?.name || "—"}
                                            </span>
                                            <span className="campaign-id">
                                                {log.campaignId?.campaignId}
                                            </span>
                                        </td>

                                        <td>{log.goalName}</td>

                                        <td className="level">{log.postbackLevel}</td>

                                        <td>
                                            <span className={`status-badge ${log.status.toLowerCase()}`}>
                                                {log.status}
                                            </span>
                                        </td>

                                        <td className="http">
                                            {log.httpStatus || "—"}
                                        </td>
                                        <td className="latency">
                                            {log.respondedAt
                                                ? `${Math.max(
                                                    0,
                                                    new Date(log.respondedAt) - new Date(log.firedAt)
                                                )} ms`
                                                : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {loading && <div className="table-loader">Loading…</div>}
                        {!hasMore && <div className="table-end">No more logs</div>}
                    </div>
                </div>

                <span className="copyright-text">
                    © 2025 Swift Digital Ads, All rights reserved
                </span>
            </main>
        </div>
    );
};

export default PostbackLogsPage;